import { defineNuxtPlugin } from '#app';
import { captureMessage } from '@sentry/vue';
import { useAuthUserStore } from '~/store/auth-user';
import { useFirebase } from '~/composables/use-firebase';

const RELOAD_GUARD_KEY = 'auth-ssr-recovery-reloaded';
const AUTH_STATE_READY_TIMEOUT_MS = 5000;

/**
 * Recovers from SSR having rendered the page logged-out while the browser
 * holds a valid Firebase session. This happens whenever the navigation request
 * bypassed the auth service worker (hard refresh, first navigation after the
 * worker was unregistered), so the server never saw an Authorization header.
 * Hydrating the logged-in client state onto logged-out server HTML corrupts
 * the layout, and protected routes bounce through the login page.
 *
 * A programmatic reload is a normal navigation, so it goes through the active
 * service worker and SSR renders authenticated. The sessionStorage guard
 * limits recovery to one reload per navigation; if the divergence persists
 * (e.g. no service worker at all), boot continues with the current client-side
 * fallback behavior. The plugin blocks further boot only in the divergent
 * case, so the mismatched app never mounts.
 */
export default defineNuxtPlugin(async (nuxtApp) => {
  const authUserStore = useAuthUserStore();
  const { auth } = useFirebase();

  if (authUserStore.isLoggedIn) {
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
      return;
    }
    return;
  }

  const isAuthStateRestored = await Promise.race([
    auth.authStateReady().then(() => true),
    new Promise<boolean>((resolve) => {
      setTimeout(() => resolve(false), AUTH_STATE_READY_TIMEOUT_MS);
    }),
  ]);

  if (!isAuthStateRestored) {
    // Sentry is initialized by a later plugin, so defer the report until the
    // app has mounted.
    nuxtApp.hook('app:mounted', () => {
      captureMessage('auth-ssr-recovery: authStateReady timed out', {
        level: 'warning',
        tags: { feature: 'auth-ssr-recovery' },
      });
    });
    return;
  }

  if (!auth.currentUser) {
    return;
  }

  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) !== null) {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  } catch {
    return;
  }

  globalThis.location.reload();
  await new Promise(() => {
    // Never resolves: halts app boot so the mismatched client state is never
    // hydrated onto the logged-out server HTML while the reload is in flight.
  });
});
